import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, BookOpen, Users, FileText, Package,
  BarChart3, Bell, Settings, LayoutTemplate, Pin, PinOff,
  Sun, Moon, LogOut, ShieldAlert, ServerCog, Tag, Sparkles, Scale, Mail, Star
} from 'lucide-react';
import { useTheme } from '@/providers/theme-provider';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

interface AdminSidebarProps {
  expanded: boolean;
  pinned: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onPinToggle: () => void;
}

const NAV_ITEMS = [
  { name: 'Overview', href: '/admin/overview', icon: LayoutDashboard },
  { name: 'Books', href: '/admin/books', icon: BookOpen },
  { name: 'Genres', href: '/admin/genres', icon: Tag },
  { name: 'Reviews', href: '/admin/reviews', icon: Star },
  { name: 'Users', href: '/admin/users', icon: Users },
  { name: 'Loans', href: '/admin/loans', icon: FileText },
  { name: 'Policies', href: '/admin/policies', icon: Scale },
  { name: 'Overdue Notices', href: '/admin/overdue-notices', icon: Mail },
  { name: 'Inventory', href: '/admin/inventory', icon: Package },
  { name: 'Conditions', href: '/admin/conditions', icon: Sparkles },
  { name: 'Reports', href: '/admin/reports', icon: BarChart3 },
  { name: 'Notifications', href: '/admin/notifications', icon: Bell },
  { name: 'Security', href: '/admin/security', icon: ShieldAlert },
  { name: 'System', href: '/admin/system', icon: ServerCog },
  { name: 'Settings', href: '/admin/settings', icon: Settings },
  { name: 'Site Content', href: '/admin/content', icon: LayoutTemplate },
];

const AdminSidebar: React.FC<AdminSidebarProps> = ({
  expanded, pinned, onMouseEnter, onMouseLeave, onPinToggle,
}) => {
  const location = useLocation();
  const { theme, setTheme } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out');
      navigate('/');
    } catch {
      toast.error('Failed to logout');
    }
  };

  const initials = user?.first_name && user?.last_name
    ? `${user.first_name[0]}${user.last_name[0]}`
    : (user?.username?.substring(0, 2) ?? 'AD').toUpperCase();

  const sidebarWidth = expanded ? 220 : 48;

  return (
    <aside
      className="fixed left-0 top-0 h-full bg-white border-r border-[#E8E4DC] z-40 flex flex-col"
      style={{ width: sidebarWidth, transition: 'width 200ms ease' }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Header */}
      <div className="flex items-center h-16 px-3 border-b border-[#E8E4DC] flex-shrink-0 overflow-hidden">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-amber-500 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
            L
          </div>
          {expanded && (
            <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">
              SMCHS Library
            </span>
          )}
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 py-3 overflow-hidden">
        {NAV_ITEMS.map(({ name, href, icon: Icon }) => {
          const active = location.pathname === href;
          return (
            <div key={name} className="relative group px-2 mb-0.5">
              <NavLink
                to={href}
                className={`flex items-center gap-3 rounded-xl px-2.5 py-2.5 transition-colors ${
                  active
                    ? 'bg-amber-100 text-amber-600'
                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                }`}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                {expanded && (
                  <span className={`text-sm font-medium whitespace-nowrap ${active ? 'text-amber-600' : 'text-gray-700'}`}>
                    {name}
                  </span>
                )}
              </NavLink>
              {/* Tooltip: only when collapsed */}
              {!expanded && (
                <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2.5 py-1.5 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
                  {name}
                  <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="border-t border-[#E8E4DC] py-3 px-2 flex-shrink-0 overflow-hidden">
        {/* Pin toggle */}
        <button
          onClick={onPinToggle}
          className="flex items-center gap-3 w-full rounded-xl px-2.5 py-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors mb-0.5"
          title={pinned ? 'Unpin sidebar' : 'Pin sidebar'}
        >
          {pinned ? <PinOff className="h-4 w-4 flex-shrink-0" /> : <Pin className="h-4 w-4 flex-shrink-0" />}
          {expanded && <span className="text-xs text-gray-500 whitespace-nowrap">{pinned ? 'Unpin' : 'Pin sidebar'}</span>}
        </button>
        {/* Theme toggle */}
        <button
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          className="flex items-center gap-3 w-full rounded-xl px-2.5 py-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors mb-0.5"
        >
          {theme === 'dark' ? <Sun className="h-4 w-4 flex-shrink-0" /> : <Moon className="h-4 w-4 flex-shrink-0" />}
          {expanded && <span className="text-xs text-gray-500 whitespace-nowrap">{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>}
        </button>
        {/* User / logout */}
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 w-full rounded-xl px-2.5 py-2 text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors"
          title="Log out"
        >
          <div className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
            {initials}
          </div>
          {expanded && <span className="text-xs text-gray-500 whitespace-nowrap truncate">Log out</span>}
        </button>
      </div>
    </aside>
  );
};

export default AdminSidebar;
